import React from 'react';
import { Box, Typography, Button, useTheme } from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  showBackButton?: boolean;
  onBack?: () => void;
  backLabel?: string;
  primaryActionLabel?: string;
  onPrimaryAction?: () => void;
  primaryIcon?: React.ReactNode;
  primaryActionDisabled?: boolean;
  secondaryActionLabel?: string;
  onSecondaryAction?: () => void;
  secondaryIcon?: React.ReactNode;
  secondaryActions?: React.ReactNode;
  isSubmitting?: boolean;
}

export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  showBackButton = false,
  onBack,
  backLabel = 'Back',
  primaryActionLabel,
  onPrimaryAction,
  primaryIcon,
  primaryActionDisabled = false,
  secondaryActionLabel,
  onSecondaryAction,
  secondaryIcon,
  secondaryActions,
  isSubmitting = false,
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2,
        flexWrap: 'wrap',
        gap: 2,
      }}
    >
      {/* Left side: Title + Subtitle */}
      <Box>
        <Typography
          variant="h4"
          sx={{
            color: theme.palette.text.primary,
            fontWeight: 600,
            letterSpacing: '0.3px',
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              mt: 0.5,
              display: 'block',
              letterSpacing: '0.2px',
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>

      {/* Right side: Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {showBackButton && (
          <Button variant="outlined" startIcon={<ArrowBack />} onClick={onBack} disabled={isSubmitting}>
            {backLabel}
          </Button>
        )}


        {secondaryActionLabel && (
          <Button variant="outlined" startIcon={secondaryIcon} onClick={onSecondaryAction} disabled={isSubmitting}>
            {secondaryActionLabel}
          </Button>
        )}

        {secondaryActions}

        {primaryActionLabel && (
          <Button
            variant="contained"
            startIcon={primaryIcon}
            onClick={onPrimaryAction}
            disabled={isSubmitting || primaryActionDisabled}
            sx={{
              backgroundColor: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              },
            }}
          >
            {isSubmitting ? 'Saving...' : primaryActionLabel}
          </Button>
        )}
      </Box>
    </Box>
  );
};
